import { HandCoins, Info } from 'lucide-react';
import { useSettlementSummary } from '@/hooks/useSettlementSummary';
import { formatSettlementDate } from './_lib';
import { formatReportMoney } from '@evi/shared';
import styles from '../DashboardPage.module.css';

/**
 * Settlement Summary widget. "What does EVI owe my building?"
 *
 * v1 ships lifetime-unsettled semantics. No SettlementBatch
 * model exists yet (filed at docs/tickets/settlement-batching.md);
 * the response carries `settlement_cadence: 'manual'` so the
 * frontend labels the total as recorded electricity cost, not an
 * unpaid balance, instead of fabricating a next-payout date that
 * Building Admins might plan around.
 *
 * Once the batch model lands, the response gains a real
 * `next_settlement_date` and `settlement_cadence: 'monthly'`,
 * and the cadence note flips to a real period framing
 * automatically — no widget rewrite needed.
 *
 * Empty-state hierarchy:
 *   1. session_count === 0 → "No unsettled sessions in scope"
 *   2. session_count > 0   → amount + scope subline + cadence note
 */
export default function SettlementSummaryWidget() {
  const q = useSettlementSummary();
  const data = q.data;
  // Collect and hold: electricity on held payouts is owed too, but pending
  // (paid manually), so it is its own line, never folded into the total.
  const heldCount = data?.held_session_count ?? 0;

  return (
    <div className={styles.chartBody}>
      <div className={styles.payoutHeader}>
        <div className={styles.chartIcon}><HandCoins size={14} /></div>
        <p className={styles.chartTitle}>Owed to building</p>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && data.session_count === 0 && heldCount === 0 && (
        <div>
          <p className={styles.payoutEmptyTitle}>No unsettled sessions</p>
          <p className={styles.payoutEmptyDesc}>
            {data.is_scoped
              ? 'No completed sessions at your assigned locations are awaiting settlement.'
              : 'No completed sessions in scope are awaiting settlement.'}
          </p>
        </div>
      )}

      {data && (data.session_count > 0 || heldCount > 0) && (
        <>
          {(data.money_by_currency ?? []).map(bucket => <div key={bucket.currency ?? 'unknown'}>
            <p className={styles.settlementAmount}>
              {formatReportMoney(bucket.total_owed_cents, bucket.currency)}
            </p>
            <p className={styles.settlementSubtitle}>
              {bucket.session_count} session{bucket.session_count === 1 ? '' : 's'}
              {bucket.oldest_session_date && ` · since ${formatSettlementDate(bucket.oldest_session_date)}`}
            </p>
            {bucket.held_session_count > 0 && (
              <p className={styles.settlementSubtitle}>
                + {formatReportMoney(bucket.held_electricity_cents, bucket.currency)} pending on {bucket.held_session_count} held session{bucket.held_session_count === 1 ? '' : 's'}, paid manually
              </p>
            )}
          </div>)}
          <SettlementCadenceNote
            cadence={data.settlement_cadence}
            nextDate={data.next_settlement_date}
          />
        </>
      )}
    </div>
  );
}

/**
 * Cadence note rendered below the dollar amount. Two states:
 *
 *   - 'manual'  → no batch date exists. The total is a lifetime sum
 *                 of recorded electricity cost that never goes down,
 *                 so the note says it is not an unpaid balance and
 *                 points to the Site Host statement's transfers.
 *   - 'monthly' → "Next settlement: <date>" with a real date.
 *                 Lights up automatically once the batch model
 *                 starts populating next_settlement_date.
 */
function SettlementCadenceNote({
  cadence, nextDate,
}: {
  cadence: 'manual' | 'monthly';
  nextDate: string | null;
}) {
  if (cadence === 'monthly' && nextDate) {
    return (
      <div className={styles.settlementCadenceNote}>
        <Info size={12} className={styles.settlementCadenceIcon} />
        <span>
          Next settlement: <strong>{formatSettlementDate(nextDate)}</strong>
        </span>
      </div>
    );
  }
  return (
    <div className={styles.settlementCadenceNote}>
      <Info size={12} className={styles.settlementCadenceIcon} />
      <span>
        Recorded electricity cost, not an unpaid balance. Per-session
        transfers are listed in each location's Site Host statement.
      </span>
    </div>
  );
}
